import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { and, not } from 'ember-truth-helpers';
import EventDetailDrawer from 'garaje/components/health-dashboard/event-detail-drawer';
import RelatedEventsDrawer from 'garaje/components/health-dashboard/related-events-drawer';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore – garaje/config/environment has no TS declarations
import config from 'garaje/config/environment';
import onDestroy from 'garaje/modifiers/on-destroy';
import onInsert from 'garaje/modifiers/on-insert';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore – JS util module
import { buildUrl, mapTimelineEvent } from 'garaje/utils/integration-health';

export interface DrawerEvent {
  eventId: string;
  installId: string | number;
  processedAtMs: number;
  processedDateDisplay: string;
  processedTimeDisplay: string;
  integrationType: string;
  integrationName: string;
  locationName: string | null;
  // Field types match child component signatures (DetailEvent / RelatedEvent) for structural compatibility.
  operationType: string;
  operationTypeLabel: string;
  operationDefinition: string | null;
  operationStatus: string;
  failureType: string | null;
  failureReason: string | null;
  displayReason: string | null;
  errorDetail: string | null;
  recommendedAction: string | null;
  helpCenterUrl: string | null;
  direction: string | null;
  responsibleParty: string | null;
  primaryMetric: string | null;
  additionalMetrics: { name: string; value: number }[] | null;
  endpoint: string | null;
  durationMillis: number;
  durationDisplay: string;
  correlationId: string | null;
  contextDetails: Record<string, string> | null;
}

interface Integration {
  installId: string | number;
  integrationName: string;
}

type StackEntry = { type: 'detail'; event: DrawerEvent } | { type: 'related'; relatedEvents: DrawerEvent[] };

interface DrawerStackSignature {
  Args: {
    integrations: Integration[];
    eventLogUrl: string;
  };
  Blocks: {
    default: [{ openDrawer: (event: DrawerEvent) => void; selectedEvent: DrawerEvent | null }];
  };
}

export default class HealthDashboardDrawerStack extends Component<DrawerStackSignature> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @service declare ajaxFetch: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @service declare state: any;

  @tracked selectedEvent: DrawerEvent | null = null;
  @tracked relatedEvents: DrawerEvent[] | null = null;
  @tracked isRelatedDrawerOpen = false;
  @tracked _drawerStack: StackEntry[] = [];
  @tracked _relatedEventsLoading = false;

  _relatedFetchGeneration = 0;

  get hasDrawerHistory(): boolean {
    return this._drawerStack.length > 0;
  }

  get detailEvent(): DrawerEvent {
    return this.selectedEvent!;
  }

  get safeRelatedEvents(): DrawerEvent[] {
    return this.relatedEvents ?? [];
  }

  get relatedEventsCount(): number {
    return this.relatedEvents?.length ?? 0;
  }

  get selectedEventCorrelationId(): string {
    return this.selectedEvent?.correlationId ?? '';
  }

  @action lockScroll(): void {
    const hasScrollBar = window.innerWidth > document.body.clientWidth;
    if (hasScrollBar) {
      document.body.style.marginRight = `${window.innerWidth - document.body.clientWidth}px`;
    }
    document.body.classList.add('overflow-hidden');
  }

  @action unlockScroll(): void {
    document.body.classList.remove('overflow-hidden');
    document.body.style.marginRight = '';
  }

  @action openDrawer(event: DrawerEvent): void {
    this.selectedEvent = event;
    this.relatedEvents = null;
    this.isRelatedDrawerOpen = false;
    this._drawerStack = [];
    this._relatedEventsLoading = false;
    if (event.correlationId) {
      this._relatedEventsLoading = true;
      void this.fetchRelatedEvents(event.correlationId, event.processedAtMs);
    }
  }

  @action closeDrawer(): void {
    this.selectedEvent = null;
    this.relatedEvents = null;
    this.isRelatedDrawerOpen = false;
    this._drawerStack = [];
  }

  async fetchRelatedEvents(correlationId: string, anchorMs?: number): Promise<void> {
    const gen = ++this._relatedFetchGeneration;
    this._relatedEventsLoading = true;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const companyId = String(this.state.currentCompany?.id);
    const anchor = anchorMs ?? Date.now();
    const startTime = anchor - 60 * 60 * 1000;
    const endTime = anchor + 60 * 60 * 1000;
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const data = await this.ajaxFetch.request(
        buildUrl(`${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v2/debugging/related`, {
          companyId,
          correlationId,
          startTime: String(startTime),
          endTime: String(endTime),
        }),
      );
      if (gen !== this._relatedFetchGeneration) return;
      const nameById = Object.fromEntries(this.args.integrations.map((i) => [String(i.installId), i.integrationName]));
      const currentEventId = this.selectedEvent?.eventId;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.relatedEvents = (data?.events ?? [])
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        .map((raw: any) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          const event = mapTimelineEvent(raw) as DrawerEvent;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          event.integrationName = nameById[String(raw.installId)] ?? event.integrationType;
          return event;
        })
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        .filter((e: DrawerEvent) => e.eventId !== currentEventId);
    } catch {
      if (gen !== this._relatedFetchGeneration) return;
      this.relatedEvents = [];
    } finally {
      if (gen === this._relatedFetchGeneration) {
        this._relatedEventsLoading = false;
      }
    }
  }

  @action openRelatedDrawer(): void {
    this._drawerStack = [...this._drawerStack, { type: 'detail', event: this.selectedEvent! }];
    this.isRelatedDrawerOpen = true;
  }

  @action closeRelatedDrawer(): void {
    if (this._drawerStack.length > 0) {
      const stack = [...this._drawerStack];
      const prev = stack.pop()!;
      this._drawerStack = stack;
      if (prev.type === 'detail') {
        this.selectedEvent = prev.event;
        this.isRelatedDrawerOpen = false;
        if (prev.event.correlationId) {
          this._relatedEventsLoading = true;
          void this.fetchRelatedEvents(prev.event.correlationId, prev.event.processedAtMs);
        }
      }
    } else {
      this.isRelatedDrawerOpen = false;
    }
  }

  @action openRelatedEventDetail(event: unknown): void {
    // RelatedEvent is a structural subset of DrawerEvent; mapTimelineEvent always returns DrawerEvent
    const drawerEvent = event as DrawerEvent;
    this._drawerStack = [...this._drawerStack, { type: 'related', relatedEvents: this.relatedEvents ?? [] }];
    this.selectedEvent = drawerEvent;
    this.isRelatedDrawerOpen = false;
    if (drawerEvent.correlationId) {
      this._relatedEventsLoading = true;
      void this.fetchRelatedEvents(drawerEvent.correlationId, drawerEvent.processedAtMs);
    }
  }

  @action drawerBack(): void {
    if (this._drawerStack.length === 0) return;
    const stack = [...this._drawerStack];
    const prev = stack.pop()!;
    this._drawerStack = stack;
    if (prev.type === 'related') {
      this.relatedEvents = prev.relatedEvents;
      this.isRelatedDrawerOpen = true;
    } else if (prev.type === 'detail') {
      this.selectedEvent = prev.event;
      this.isRelatedDrawerOpen = false;
      if (prev.event.correlationId) {
        this._relatedEventsLoading = true;
        void this.fetchRelatedEvents(prev.event.correlationId, prev.event.processedAtMs);
      }
    }
  }

  @action closeActiveDrawer(): void {
    this.selectedEvent = null;
    this.relatedEvents = null;
    this.isRelatedDrawerOpen = false;
    this._drawerStack = [];
  }

  <template>
    {{yield (hash openDrawer=this.openDrawer selectedEvent=this.selectedEvent)}}

    {{#if this.selectedEvent}}
      <div
        class="fixed inset-0 z-[996] bg-black/20 cursor-pointer !mt-0"
        role="button"
        {{onInsert this.lockScroll}}
        {{onDestroy this.unlockScroll}}
        {{on "click" this.closeActiveDrawer}}
      ></div>
    {{/if}}

    {{#if (and this.selectedEvent (not this.isRelatedDrawerOpen))}}
      <EventDetailDrawer
        @event={{this.detailEvent}}
        @onClose={{if this.hasDrawerHistory this.drawerBack this.closeDrawer}}
        @relatedEventsLoading={{this._relatedEventsLoading}}
        @relatedEventsCount={{this.relatedEventsCount}}
        @onOpenRelatedDrawer={{this.openRelatedDrawer}}
        @showBackArrow={{this.hasDrawerHistory}}
        @onCloseAll={{this.closeDrawer}}
        @eventLogUrl={{@eventLogUrl}}
      />
    {{/if}}

    {{#if this.isRelatedDrawerOpen}}
      <RelatedEventsDrawer
        @events={{this.safeRelatedEvents}}
        @correlationId={{this.selectedEventCorrelationId}}
        @onClose={{this.closeRelatedDrawer}}
        @onCloseAll={{this.closeDrawer}}
        @onOpenEventDetail={{this.openRelatedEventDetail}}
      />
    {{/if}}
  </template>
}
